// Импортируем плагины в самом начале, чтобы гарантировать их инициализацию
import './plugins';

import { createRxDatabase, RxDatabase, RxCollection } from 'rxdb';
import { getRxStorageDexie } from 'rxdb/plugins/storage-dexie';
import { DocumentSchemaType, documentSchema } from './schema';

// Определение типов для базы данных и коллекций
export type DocumentCollection = RxCollection<DocumentSchemaType>;

export type RxDB = RxDatabase<{
  documents: DocumentCollection;
}>;

let dbPromise: Promise<RxDB> | null = null;

/**
 * Инициализирует базу данных, если она еще не инициализирована
 * @returns Promise с инициализированной базой данных
 */
export const getDatabase = async (): Promise<RxDB> => {
  
  if (!dbPromise) {
    dbPromise = createDatabase();
  }
  
  const result = await dbPromise;
  
  return result;
};

/**
 * Создает базу данных с необходимыми коллекциями
 * @returns Promise с созданной базой данных
 */
const createDatabase = async (): Promise<RxDB> => {
  
  try {
    const db = await createRxDatabase<{
      documents: DocumentCollection;
    }>({
      name: 'tiptap_autosave_db',
      storage: getRxStorageDexie()
    });
    

    // Создаем коллекцию документов
    await db.addCollections({
      documents: {
        schema: documentSchema
      }
    });
    

    return db;
  } catch (error) {
    
    // Если ошибка связана с существующей базой данных, попробуем пересоздать её
    if (error instanceof Error && error.message && error.message.includes('already exists')) {
     
      throw error; // Пробрасываем ошибку дальше для повторной попытки
    }
    
    throw error;
  }
};

/**
 * Функция для очистки и пересоздания базы данных
 * @returns Promise с новой базой данных
 */
export const recreateDatabase = async (): Promise<RxDB> => {
  
  // Сбрасываем существующий promise
  dbPromise = null;
  
  // Очищаем IndexedDB (удаляем базу данных)
  try {
    if (typeof window !== 'undefined' && window.indexedDB) {
      const deleteReq = window.indexedDB.deleteDatabase('tiptap_autosave_db');
      await new Promise((resolve, reject) => {
        deleteReq.onsuccess = () => resolve(undefined);
        deleteReq.onerror = () => reject(deleteReq.error);
        deleteReq.onblocked = () => {
          resolve(undefined);
        };
      });
    }
  } catch (deleteError) {
  }
  
  // Создаем новую базу данных
  return getDatabase();
};

/**
 * Функция для удаления документов по стратегии LRU (Least Recently Used)
 * при превышении лимита в 15 документов
 * @param db База данных RxDB
 */
export const cleanupLRUDocuments = async (db: RxDB): Promise<void> => {
  const MAX_DOCUMENTS = 15;
  
  // Получаем все документы, отсортированные по времени последнего доступа (от старых к новым)
  const documents = await db.documents
    .find()
    .sort({ lastAccessed: 'asc' })
    .exec();
  
  // Если количество документов превышает максимальное - удаляем самые старые
  if (documents.length > MAX_DOCUMENTS) {
    const docsToRemove = documents.slice(0, documents.length - MAX_DOCUMENTS);
    
    // Удаляем каждый документ
    for (const doc of docsToRemove) {
      await doc.remove();
    }
    
  }
}; 